import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import companyService from "@/services/company";
import {
  dashboardService,
  TicketsByClientReportRow,
  WhatsappMessagesReportRow,
} from "@/services/dashboard.service";
import { Company } from "@/types/company";
import { BarChart3, MessageCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

/** "YYYY-MM" do mês atual, no fuso local. */
function currentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(month: string): string {
  const [year, m] = month.split("-").map(Number);
  const date = new Date(year, (m || 1) - 1, 1);
  const label = date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "YYYY-MM" -> range [1º dia 00:00, último dia 23:59:59.999] no fuso local. */
function monthToRange(monthValue: string): { from: Date; to: Date } {
  const [year, m] = monthValue.split("-").map(Number);
  const from = new Date(year, (m || 1) - 1, 1, 0, 0, 0, 0);
  const to = new Date(year, m || 1, 0, 23, 59, 59, 999);
  return { from, to };
}

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

/**
 * Substitui o `<input type="month">` nativo, que sempre exibe os nomes de mês no idioma do
 * navegador/SO (em inglês pra maioria dos usuários) e ignora o `lang` da página — não dá pra
 * forçar localização nesse controle nativo.
 */
function MonthYearSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const [year, month] = value.split("-");
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => String(currentYear - 4 + i));

  return (
    <div className="flex gap-2">
      <Select value={month} onValueChange={(m) => onChange(`${year}-${m}`)}>
        <SelectTrigger id={id} className="flex-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_NAMES.map((name, idx) => (
            <SelectItem key={name} value={String(idx + 1).padStart(2, "0")}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={year} onValueChange={(y) => onChange(`${y}-${month}`)}>
        <SelectTrigger className="w-24">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y} value={y}>
              {y}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function ReportTicketsByClient() {
  const { toast } = useToast();

  const [fromMonth, setFromMonth] = useState(currentMonthValue());
  const [toMonth, setToMonth] = useState(currentMonthValue());
  const [clientId, setClientId] = useState<string>("all");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [rows, setRows] = useState<TicketsByClientReportRow[]>([]);
  const [whatsappRows, setWhatsappRows] = useState<WhatsappMessagesReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    companyService.getAll(undefined, 1, 500).then(([data]) => {
      if (data) setCompanies(data.result || []);
    });
  }, []);

  const fetchReport = async () => {
    setIsLoading(true);
    try {
      const { from } = monthToRange(fromMonth);
      const { to } = monthToRange(toMonth);
      const [ticketsData, whatsappData] = await Promise.all([
        dashboardService.getTicketsByClientReport({
          from,
          to,
          clientId: clientId === "all" ? undefined : clientId,
        }),
        dashboardService.getWhatsappMessagesReport({ from, to }),
      ]);
      setRows(ticketsData);
      setWhatsappRows(whatsappData);
    } catch {
      toast({ title: "Erro", description: "Falha ao carregar o relatório", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMonth, toMonth, clientId]);

  const groupedByMonth = useMemo(() => {
    const map = new Map<string, TicketsByClientReportRow[]>();
    for (const row of rows) {
      const list = map.get(row.month) || [];
      list.push(row);
      map.set(row.month, list);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [rows]);

  const grandTotal = useMemo(
    () => ({
      opened: rows.reduce((acc, r) => acc + r.ticketsOpened, 0),
      closed: rows.reduce((acc, r) => acc + r.ticketsClosed, 0),
      hours: Math.round(rows.reduce((acc, r) => acc + r.totalHours, 0) * 100) / 100,
    }),
    [rows],
  );

  const whatsappTotal = useMemo(
    () => ({
      sent: whatsappRows.reduce((acc, r) => acc + r.sent, 0),
      received: whatsappRows.reduce((acc, r) => acc + r.received, 0),
    }),
    [whatsappRows],
  );

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Tickets e horas por cliente
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Abertos = criados no mês; Fechados = resolvidos no mês (podem ser de meses diferentes).
            Horas vêm do tempo informado pelo agente ao fechar o ticket.
          </p>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fromMonth">De</Label>
              <MonthYearSelect id="fromMonth" value={fromMonth} onChange={setFromMonth} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="toMonth">Até</Label>
              <MonthYearSelect id="toMonth" value={toMonth} onChange={setToMonth} />
            </div>
            <div className="space-y-2">
              <Label>Cliente</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os clientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Nenhum ticket aberto ou resolvido no período selecionado.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedByMonth.map(([month, monthRows]) => {
            const monthOpened = monthRows.reduce((acc, r) => acc + r.ticketsOpened, 0);
            const monthClosed = monthRows.reduce((acc, r) => acc + r.ticketsClosed, 0);
            const monthTotalHours =
              Math.round(monthRows.reduce((acc, r) => acc + r.totalHours, 0) * 100) / 100;
            return (
              <Card key={month}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{monthLabel(month)}</span>
                    <span className="text-sm font-normal text-muted-foreground">
                      {monthOpened} aberto{monthOpened !== 1 ? "s" : ""} · {monthClosed} fechado
                      {monthClosed !== 1 ? "s" : ""} · {monthTotalHours}h
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Cliente</TableHead>
                        <TableHead className="text-right">Abertos</TableHead>
                        <TableHead className="text-right">Fechados</TableHead>
                        <TableHead className="text-right">Horas</TableHead>
                        <TableHead className="text-right">Sem horas informadas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthRows
                        .sort((a, b) => b.ticketsClosed - a.ticketsClosed)
                        .map((row) => (
                          <TableRow key={`${row.month}-${row.clientId || "none"}`}>
                            <TableCell className="font-medium">{row.clientName}</TableCell>
                            <TableCell className="text-right">{row.ticketsOpened}</TableCell>
                            <TableCell className="text-right">{row.ticketsClosed}</TableCell>
                            <TableCell className="text-right">{row.totalHours}h</TableCell>
                            <TableCell className="text-right">
                              {row.ticketsWithoutHours > 0 ? (
                                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                                  {row.ticketsWithoutHours}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}

          <Card>
            <CardContent className="py-4 flex flex-wrap justify-end gap-6 text-sm">
              <span className="text-muted-foreground">Total do período:</span>
              <span className="font-medium">{grandTotal.opened} abertos</span>
              <span className="font-medium">{grandTotal.closed} fechados</span>
              <span className="font-medium">{grandTotal.hours}h</span>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="mt-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="h-4 w-4" />
            Mensagens de WhatsApp da organização
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Total da organização (não por cliente), no mesmo período filtrado acima.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : whatsappRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhuma mensagem de WhatsApp no período.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mês</TableHead>
                  <TableHead className="text-right">Enviadas</TableHead>
                  <TableHead className="text-right">Recebidas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {whatsappRows.map((row) => (
                  <TableRow key={row.month}>
                    <TableCell className="font-medium">{monthLabel(row.month)}</TableCell>
                    <TableCell className="text-right">{row.sent}</TableCell>
                    <TableCell className="text-right">{row.received}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <tfoot>
                <TableRow>
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell className="text-right font-medium">{whatsappTotal.sent}</TableCell>
                  <TableCell className="text-right font-medium">{whatsappTotal.received}</TableCell>
                </TableRow>
              </tfoot>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}
